import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiProhibitLight,
} from 'react-icons/pi';
import BrandLoader from '../BrandLoader';
import Alert from '../../TailwindComponents/Alert';
import Button from '../../TailwindComponents/Button';
import AIProgressBar from './AIProgressBar';
import AIStageList from './AIStageList';
import AISubItemList from './AISubItemList';
import { AIRun, displayStages, isRunFinished, isRunLive } from './aiRun';

/**
 * THE full-surface view of an AI run.
 *
 * One panel for matter creation, matter analysis, index and table creation,
 * template structure generation, bulk import and "find more" - flows that
 * previously each drew their own `bg-white dark:bg-gray-800` card with a
 * `bg-gradient-to-r from-info-500 to-info-900` bar over a client-invented
 * percentage.
 *
 * What it will not do:
 *  - draw a stage the engine has not mentioned;
 *  - print a percentage the engine did not send (no number → moving sliver);
 *  - go silent between "submitted" and the first event (connecting stage).
 */

const pretty = (s: number) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);

export interface AIProgressPanelProps {
  run: AIRun;
  /** Header line while running. Pass an already-translated string. */
  title?: string;
  /** Header lines for the terminal states. */
  completedTitle?: string;
  failedTitle?: string;
  cancelledTitle?: string;
  /** Quiet line under the header when the engine has said nothing yet. */
  idleMessage?: string;
  /** Optional flavour glyph per stage key - see `AIStageList`. */
  iconForKey?: Record<string, React.ReactNode>;
  /** Header for the per-item list, or the text of the one-line summary. */
  subItemsLabel?: string;
  /** Sub-items are filenames. Default true. */
  subItemFileIcons?: boolean;
  /**
   * What to do with the items the run was handed.
   *
   *  - `list` (default) - a row each, with its own status and bar. Right for a
   *    pipeline that reports per-file progress (review, tables, playbooks).
   *  - `summary` - one quiet "8 documents" line beside the step count. Right
   *    for a pipeline that processes the batch in BULK and never says a word
   *    about any individual file: matter creation used to draw eight rows that
   *    all sat at "Waiting" until the run ended.
   *  - `none` - nothing.
   */
  subItemsDisplay?: 'list' | 'summary' | 'none';
  /**
   * Print the engine's percentage as a number in the header. Default true.
   * `false` leaves the bar as the only reading of "how far along", which is
   * what a flow wants whose own step messages already carry a percentage.
   */
  showPercent?: boolean;
  /**
   * `showcase` is the reading for a run that IS the screen: a railed stage
   * list, plain glass while the run is live, no line printed twice - and
   * generous space, because a surface someone sits and watches for minutes
   * should feel calm rather than packed. `comfortable` (default) is the
   * unchanged in-page reading. (This value was `compact`; the rail and the
   * de-duplicated lines were always the point, the tight spacing never was.)
   */
  density?: 'comfortable' | 'showcase';
  /** Last-mile polish on engine sentences. Identity when not passed. */
  formatMessage?: (message: string) => string;
  /** Wired to a Cancel button in the header while the run is live. */
  onCancel?: () => void;
  onDismissFailedItems?: () => void;
  /**
   * `centered` gives the panel the content area and floats it in the middle at
   * a reading width - a run is usually the only thing on screen while it goes.
   */
  layout?: 'inline' | 'centered';
  /** Controls that belong WITH the card - e.g. "Continue in background". */
  footer?: React.ReactNode;
  /** Note under the card while running. Defaults to the "few minutes" line. */
  hint?: React.ReactNode;
  /** Collapse the trace to a one-line summary once the run finishes. */
  collapseOnComplete?: boolean;
  className?: string;
}

const AIProgressPanel: React.FC<AIProgressPanelProps> = ({
  run,
  title,
  completedTitle,
  failedTitle,
  cancelledTitle,
  idleMessage,
  iconForKey,
  subItemsLabel,
  subItemFileIcons,
  subItemsDisplay = 'list',
  showPercent = true,
  density = 'comfortable',
  formatMessage,
  onCancel,
  onDismissFailedItems,
  layout = 'inline',
  footer,
  hint,
  collapseOnComplete = false,
  className,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);

  const centered = layout === 'centered';
  const showcase = density === 'showcase';
  const finished = isRunFinished(run);
  const live = isRunLive(run);
  const failed = run.status === 'failed';
  const cancelled = run.status === 'cancelled';
  const partial = run.status === 'completed_with_errors';
  const succeeded = run.status === 'completed' || partial;
  const stages = displayStages(run);

  const headline = failed
    ? failedTitle || t('Processing failed')
    : cancelled
      ? cancelledTitle || t('Cancelled')
      : partial
        ? completedTitle || t('Completed with issues')
        : succeeded
          ? completedTitle || t('Complete')
          : title || t('Working…');

  const elapsed = run.elapsedSeconds > 0 ? pretty(run.elapsedSeconds) : null;
  const polish = (text: string) => (formatMessage ? formatMessage(text) : text);

  /* The engine's last line is usually the open stage's own line. Printing it
     twice - once as the headline's subtitle, once on the stage - is most of
     what made this card look repetitive, so a showcase panel keeps it on the
     stage, where it says WHICH step is talking. */
  const echoesStage = (text: string | null): boolean =>
    showcase && !!text && stages.some((s) => s.message === text || s.narration === text);
  const headerMessage = echoesStage(run.message) ? null : run.message;
  const headerNarration = echoesStage(run.narration) ? null : run.narration;
  const statusLine =
    run.error ||
    (headerMessage ? polish(headerMessage) : null) ||
    (run.status === 'connecting' ? t('Connecting to the processing pipeline…') : idleMessage) ||
    null;

  /* The two quiet counts that sit between the bar and the stages. Both are the
     engine's own numbers - the step index it reported, and how many items the
     submit handed it. */
  const stepText =
    run.stepTotal > 0
      ? t('Step {{current}} of {{total}}', {
          current: Math.min(run.stepTotal, Math.max(run.stepCurrent, 1)),
          total: run.stepTotal,
        })
      : null;
  const summaryText =
    subItemsDisplay === 'summary' && run.subItems.length > 0
      ? subItemsLabel ??
        (run.subItems.length === 1
          ? t('1 document')
          : t('{{count}} documents', { count: run.subItems.length }))
      : null;

  /* ── Collapsed summary once the run is over ─────────────────────── */
  if (collapseOnComplete && finished && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        data-testid="ai-progress-summary"
        className={clsx(
          'flex items-center gap-2 text-xs text-fg-muted hover:text-fg-strong',
          'transition-colors duration-200 ease-slide',
          className,
        )}
      >
        {failed || partial ? (
          <PiWarningCircleLight
            className={clsx('w-3.5 h-3.5', failed ? 'text-danger-500' : 'text-warning-500')}
            aria-hidden
          />
        ) : cancelled ? (
          <PiProhibitLight className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
        ) : (
          <PiCheckCircleLight className="w-3.5 h-3.5 text-success-500" aria-hidden />
        )}
        <span>
          {headline}
          {elapsed ? ` ${t('in')} ${elapsed}` : ''}
        </span>
        {stages.length > 0 && (
          <span className="text-fg-muted/70">· {t('Show details')}</span>
        )}
      </button>
    );
  }

  /*
    The panel is a column that NEVER outgrows the viewport.

    A run with a dozen stages and a list of documents used to keep growing
    until the head of it - the state, the clock, the Cancel button - was off
    the top of the screen and the footer off the bottom. So the head and the
    tail are fixed and the trace between them scrolls on its own.

    And it is not a card: an AI run is the whole screen while it goes, so it
    sits on the page's own background with no border, no glass and no tinted
    surface. State is carried by the glyph and the headline, which is where
    people were reading it from anyway.
  */
  const panel = (
    <div
      className={clsx(
        'flex flex-col min-h-0',
        centered ? 'max-h-[calc(100vh-6rem)]' : 'max-h-[80vh]',
        // A showcase run gets a slightly wider column - still a reading width,
        // still centred, just not a card squeezed around its own text.
        centered ? (showcase ? 'w-full max-w-2xl' : 'w-full max-w-xl') : undefined,
        !centered && className,
      )}
      data-testid="ai-progress-panel"
      data-run-status={run.status}
    >
      <div className={clsx('shrink-0', showcase ? 'space-y-6' : 'space-y-4')}>
        {/* Headline + overall bar */}
        <div
          className={clsx(
            'flex',
            showcase ? 'gap-4' : 'gap-3',
            // A headline on its own centres against the glyph; a headline with
            // a line under it hangs from the top of it.
            statusLine || headerNarration ? 'items-start' : 'items-center',
          )}
        >
          <span
            className={clsx(
              'rounded-lg bg-surface-sunken/70 flex items-center justify-center shrink-0',
              showcase ? 'h-11 w-11' : 'h-9 w-9',
            )}
          >
            {failed ? (
              <PiWarningCircleLight className="w-5 h-5 text-danger-500" />
            ) : partial ? (
              <PiWarningCircleLight className="w-5 h-5 text-warning-500" />
            ) : cancelled ? (
              <PiProhibitLight className="w-5 h-5 text-fg-muted" />
            ) : succeeded ? (
              <PiCheckCircleLight className="w-5 h-5 text-success-500" />
            ) : (
              <BrandLoader size="sm" />
            )}
          </span>

          <div className={clsx('flex-1 min-w-0', showcase && 'space-y-1.5')}>
            <p
              className={clsx(
                'font-medium text-fg-strong',
                showcase ? 'text-base' : 'text-sm',
              )}
            >
              {headline}
            </p>
            {/* Primary line: whatever the engine last said it was doing. An
                empty one is left out rather than reserving a blank line. */}
            {statusLine && (
              <p
                className={clsx(
                  'break-words',
                  showcase ? 'text-sm leading-relaxed text-fg-body' : 'text-xs text-fg-muted',
                )}
              >
                {statusLine}
              </p>
            )}
            {/* Secondary line: the engine's OPTIONAL narration - the aside, at
                a tone that is quieter than the status line without being
                unreadable. Nothing renders here for a flow whose engine
                doesn't send the field. */}
            {!finished && headerNarration && headerNarration !== headerMessage && (
              <p
                className={clsx(
                  'break-words',
                  showcase
                    ? 'text-xs leading-relaxed text-fg-muted'
                    : 'mt-0.5 text-[11px] text-fg-muted/70',
                )}
              >
                {polish(headerNarration)}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {elapsed && <span className="text-xs text-fg-muted tabular-nums">{elapsed}</span>}
            {/* A number ONLY when the engine sent one, and only where the
                caller wants a number at all. */}
            {showPercent && run.percent !== null && !failed && !cancelled && (
              <span className="text-xs text-fg-muted tabular-nums">{run.percent}%</span>
            )}
            {onCancel && live && (
              <Button variant="neutral" size="small" onClick={onCancel}>
                {t('Cancel')}
              </Button>
            )}
          </div>
        </div>

        {!failed && !cancelled && (
          <AIProgressBar percent={run.status === 'connecting' ? null : run.percent} />
        )}
      </div>

      {/* The trace: the only part allowed to grow, and the only part that
          scrolls. `min-h-0` is what lets it shrink inside the capped column
          instead of pushing the footer off the bottom. */}
      <div
        className={clsx(
          'flex-1 min-h-0 overflow-y-auto custom-scrollbar-main',
          showcase ? 'space-y-6 py-6' : 'space-y-4 py-4',
        )}
      >
        {/* Stages - however many the engine reports, never more */}
        {(stages.length > 0 || stepText || summaryText) && (
          <div className={showcase ? 'space-y-4' : 'space-y-2'}>
            {(stepText || summaryText) && (
              <p
                className={clsx(
                  'flex items-center gap-1.5 text-fg-muted',
                  showcase ? 'text-xs' : 'text-[11px]',
                )}
                data-testid="ai-progress-meta"
              >
                {stepText && <span>{stepText}</span>}
                {stepText && summaryText && <span aria-hidden className="opacity-50">·</span>}
                {summaryText && <span>{summaryText}</span>}
              </p>
            )}
            {stages.length > 0 && (
              <AIStageList
                stages={stages}
                iconForKey={iconForKey}
                connector={showcase}
                density={showcase ? 'showcase' : 'default'}
                formatMessage={formatMessage}
              />
            )}
          </div>
        )}

        {subItemsDisplay === 'list' && run.subItems.length > 0 && (
          <AISubItemList
            items={run.subItems}
            label={subItemsLabel}
            fileIcons={subItemFileIcons}
          />
        )}

        {/* The quiet half of a good run: it finished, but not on everything.
            It scrolls with the trace because the list of names it carries is
            as long as the run was unlucky. */}
        {run.failedItems.length > 0 && (
          <div className="[&_.alert]:min-w-0 [&_.alert]:max-w-none [&_.alert_p+p]:whitespace-pre-line">
            <Alert
              type="warning"
              message={
                run.failedItems.length === 1
                  ? t('One document could not be read')
                  : t('{{count}} documents could not be read', { count: run.failedItems.length })
              }
              subMessage={run.failedItems
                .map((d) => (d.reason ? `${d.name} - ${d.reason}` : d.name))
                .join('\n')}
              onClose={onDismissFailedItems}
            />
          </div>
        )}
      </div>

      {/* The tail: the hint and whatever gets you off this screen. Fixed, so a
          long run never buries its own way out. */}
      <div className={clsx('shrink-0', showcase ? 'space-y-4' : 'space-y-3')}>
        {!finished && (hint ?? (
          <p className={clsx('text-fg-muted', showcase ? 'text-sm' : 'text-xs')}>
            {t('This may take a few minutes. You can close this window.')}
          </p>
        ))}

        {footer && (
          <div
            className={clsx(
              'flex gap-2',
              showcase && 'pt-1',
              centered ? 'justify-center' : 'justify-end',
            )}
          >
            {footer}
          </div>
        )}

        {collapseOnComplete && finished && expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[11px] text-fg-muted hover:text-fg-strong transition-colors duration-200 ease-slide"
          >
            {t('Hide details')}
          </button>
        )}
      </div>
    </div>
  );

  if (!centered) return panel;

  return (
    <div
      className={clsx(
        // Fills whatever the parent gives it and floats the card in the middle
        // of that - no `w-[50vw]`, no viewport arithmetic.
        'w-full flex-1 flex flex-col items-center justify-center',
        showcase ? 'px-6 py-12' : 'px-4 py-10',
        className,
      )}
    >
      {panel}
    </div>
  );
};

export default AIProgressPanel;
